import { A, useNavigate } from "@solidjs/router";
import type { EvalBatchDetailResponse } from "contracts/api/evals/batches";
import { EVAL_COMPARISON_STATUSES } from "contracts/enums/evals";
import { For, Show } from "solid-js";
import {
  EVAL_BATCH_STATUS_COPY,
  EVAL_BATCH_STATUS_TONE,
  EVAL_COMPARISON_STATUS_COPY,
  EVAL_COMPARISON_STATUS_TONE,
  EVIDENCE_STATE_COPY,
  EVIDENCE_STATE_TONE,
} from "../../enums/evals";
import { relativeAge } from "../../ui/format";
import { evidenceState, winnerLabel } from "./comparisonState";
import { createEvalBatchDetail } from "./evalQueries";

/**
 * One batch: its two variants, the progress of every comparison in it, and the
 * standing winner per page. Polls while the batch is running — the backend's
 * own tick is what advances it, so this surface only watches.
 */
export function EvalBatchDetail(props: { batchId: string }) {
  const navigate = useNavigate();
  const batch = createEvalBatchDetail(() => props.batchId);

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href="/evals" class="landing-brand">
          ← Evals
        </A>
        <span class="landing-tag">Batch</span>
      </div>
      <div class="landing-main">
        <Show
          when={batch.data}
          fallback={<div class="loading">Loading batch…</div>}
        >
          {(data) => (
            <>
              <BatchHead batch={data()} />
              <section class="panel">
                <div class="panel-head">
                  <span class="panel-title">Comparisons</span>
                  <span class="badge">{data().comparisons.length}</span>
                </div>
                <table class="tbl tbl-link">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Status</th>
                      <th>Evidence</th>
                      <th>Standing winner</th>
                      <th>Runs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data().comparisons}>
                      {(comparison) => (
                        <tr
                          class="tbl-row"
                          onClick={() =>
                            navigate(
                              `/evals/comparisons/${comparison.comparisonId}`,
                            )
                          }
                        >
                          <td class="mono">
                            {comparison.originUrl}
                            <Show when={comparison.error}>
                              {(message) => (
                                <div class="gate-error">{message()}</div>
                              )}
                            </Show>
                          </td>
                          <td>
                            <span
                              class={`badge badge-status ${EVAL_COMPARISON_STATUS_TONE[comparison.status]}`}
                            >
                              {EVAL_COMPARISON_STATUS_COPY[comparison.status]}
                            </span>
                          </td>
                          <td>
                            <span
                              class={`badge badge-status ${EVIDENCE_STATE_TONE[evidenceState(comparison.evidenceMatches)]}`}
                            >
                              {
                                EVIDENCE_STATE_COPY[
                                  evidenceState(comparison.evidenceMatches)
                                ]
                              }
                            </span>
                          </td>
                          <td>
                            <Show
                              when={comparison.winner}
                              fallback={
                                <span class="landing-sub">Not judged yet</span>
                              }
                            >
                              {(winner) => (
                                <>
                                  {winnerLabel(winner(), {
                                    a: data().variantA.label,
                                    b: data().variantB.label,
                                  })}
                                </>
                              )}
                            </Show>
                          </td>
                          <td class="mono landing-sub">
                            {comparison.runAId ?? "—"} /{" "}
                            {comparison.runBId ?? "—"}
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </section>
            </>
          )}
        </Show>
      </div>
    </main>
  );
}

function BatchHead(props: { batch: EvalBatchDetailResponse }) {
  return (
    <>
      <div class="landing-head-row">
        <div>
          <h1 class="landing-h1">{props.batch.suiteName}</h1>
          <p class="landing-sub pg-lede">
            <span
              class={`badge badge-status ${EVAL_BATCH_STATUS_TONE[props.batch.status]}`}
            >
              {EVAL_BATCH_STATUS_COPY[props.batch.status]}
            </span>{" "}
            started {relativeAge(props.batch.createdAt)}
          </p>
        </div>
        <A href={`/evals/suites/${props.batch.suiteId}`} class="btn">
          Suite
        </A>
      </div>
      <section class="panel">
        <div class="panel-head">
          <span class="panel-title">Variants</span>
        </div>
        <table class="tbl">
          <thead>
            <tr>
              <th>Side</th>
              <th>Label</th>
              <th>Agent model</th>
              <th>Prompt</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>A</td>
              <td>{props.batch.variantA.label}</td>
              <td class="mono">{props.batch.variantA.agentModel}</td>
              <td class="mono landing-sub">
                {props.batch.variantA.promptBundleId ?? "active default"}
              </td>
            </tr>
            <tr>
              <td>B</td>
              <td>{props.batch.variantB.label}</td>
              <td class="mono">{props.batch.variantB.agentModel}</td>
              <td class="mono landing-sub">
                {props.batch.variantB.promptBundleId ?? "active default"}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
      <section class="panel">
        <div class="panel-head">
          <span class="panel-title">Progress</span>
        </div>
        <div class="panel-body">
          <For each={EVAL_COMPARISON_STATUSES}>
            {(status) => (
              <span class="badge">
                {EVAL_COMPARISON_STATUS_COPY[status]}:{" "}
                {props.batch.comparisonCounts[status] ?? 0}
              </span>
            )}
          </For>
        </div>
      </section>
    </>
  );
}
